// DOM half of the cinematic player: runs the animation-frame loop that
// drives the director (camera + cues), and draws the screen-side cues —
// letterbox bars, golden grade, bloom flash, particles, the banner, the
// fade — plus the Skip button. Any key, click or tap skips, from the first
// frame. The overlay covers the whole screen while it plays, so the click
// that skips never reaches the map underneath.

import {
  Building2,
  Castle,
  Church,
  Crown,
  Flag,
  Handshake,
  Hourglass,
  House,
  Landmark,
  type LucideIcon,
  MapPin,
  SkipForward,
  Sparkles,
  Star,
  Sword,
  Swords,
  Trophy,
  WandSparkles,
} from 'lucide-react';
import { type CSSProperties, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { type Translate, useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import { type MilestoneIcon, copyFor } from './copy.js';
import type { ActiveCinematic, CinematicDirector } from './director.js';
import { type VisualState, visualStateAt } from './timeline.js';
import type { ParticleKind } from './types.js';

export const MILESTONE_ICONS: Record<MilestoneIcon, LucideIcon> = {
  house: House,
  building: Building2,
  castle: Castle,
  mapPin: MapPin,
  flag: Flag,
  crown: Crown,
  trophy: Trophy,
  sword: Sword,
  sparkles: Sparkles,
  church: Church,
  landmark: Landmark,
  wand: WandSparkles,
  handshake: Handshake,
  swords: Swords,
  hourglass: Hourglass,
  star: Star,
};

interface CinematicOverlayProps {
  readonly director: CinematicDirector;
  readonly localPlayer: number;
}

export function CinematicOverlay({ director, localPlayer }: CinematicOverlayProps) {
  const state = useSyncExternalStore(director.subscribe, director.getState, director.getState);
  const active = state.active;
  if (!active) return null;
  return (
    <CinematicPlayer
      key={active.id}
      director={director}
      active={active}
      localPlayer={localPlayer}
    />
  );
}

function CinematicPlayer({
  director,
  active,
  localPlayer,
}: CinematicOverlayProps & { readonly active: ActiveCinematic }) {
  const t = useT();
  const [frame, setFrame] = useState(0);

  // Frame loop: the director moves the camera and ends the cinematic; the
  // overlay re-renders its cues from the director's clock.
  useEffect(() => {
    let raf = 0;
    const step = () => {
      director.frame();
      setFrame((n) => n + 1);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [director]);
  const elapsed = director.elapsedMs();

  // Cinematic mode: globals.css hides the HUD while this attribute is set.
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.cinematic = 'playing';
    return () => {
      delete root.dataset.cinematic;
    };
  }, []);

  // Any key skips. Capture phase + stopImmediatePropagation, so the key
  // doesn't also reach the game's shortcuts (Space would re-pause).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      director.skip();
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [director]);

  const vs = visualStateAt(active.timeline, elapsed);
  const copy = useMemo(
    () => copyFor(active.milestones, t, localPlayer),
    [active.milestones, t, localPlayer],
  );
  const kid = active.uiMode === 'icons';

  return (
    <div
      className="cinematic-overlay fixed inset-0 z-[60] select-none overflow-hidden"
      data-testid="cinematic-overlay"
      {...appDom({ id: 'cinematic-overlay', type: 'Custom', scope: 'dialog' })}
      data-recipe={active.recipeId}
      data-reduced-motion={active.timeline.reducedMotion ? 'true' : 'false'}
      onClick={() => director.skip()}
    >
      <ScreenGrade vs={vs} letterboxed={vs.letterbox} />
      {vs.particles && <Particles kind={vs.particles} />}
      {vs.banner && (
        <Banner
          title={copy.title}
          subtitle={vs.banner.subtitle ? copy.subtitle : null}
          kidLine={kid ? copy.kidLine : null}
          icon={MILESTONE_ICONS[copy.icon]}
          kid={kid}
          reducedMotion={active.timeline.reducedMotion}
          t={t}
        />
      )}
      {/* Off on the very first frame so the bars slide in. */}
      <Letterbox on={vs.letterbox && frame > 0} />
      <div className="absolute right-3 bottom-3 z-10 flex items-center gap-3 sm:right-5 sm:bottom-4">
        <span className="hidden text-xs text-white/70 sm:inline" aria-hidden="true">
          {t('cinematic.skipHint')}
        </span>
        <button
          type="button"
          data-testid="cinematic-skip"
          {...appDom({ id: 'cinematic-skip', type: 'Button', role: 'button', scope: 'dialog' })}
          onClick={(e) => {
            e.stopPropagation();
            director.skip();
          }}
          // biome-ignore lint/a11y/noAutofocus: the cinematic takes over the screen; focus the only control on it
          autoFocus
          className="inline-flex items-center gap-1.5 rounded-full border border-white/30 bg-black/55 px-3.5 py-1.5 text-sm font-medium text-white shadow-lg backdrop-blur-sm transition-colors hover:bg-black/75 focus-visible:outline-2 focus-visible:outline-amber-300"
        >
          {t('cinematic.skip')}
          <SkipForward className="size-4" aria-hidden="true" />
        </button>
      </div>
      <div
        className="pointer-events-none absolute inset-0 z-20 bg-black"
        style={{ opacity: vs.fade }}
        aria-hidden="true"
      />
    </div>
  );
}

function Letterbox({ on }: { readonly on: boolean }) {
  const bar =
    'absolute inset-x-0 h-[11vh] max-h-28 bg-black transition-transform duration-500 ease-out';
  return (
    <div className="pointer-events-none" aria-hidden="true">
      <div className={`${bar} top-0 ${on ? 'translate-y-0' : '-translate-y-full'}`} />
      <div className={`${bar} bottom-0 ${on ? 'translate-y-0' : 'translate-y-full'}`} />
    </div>
  );
}

/** Golden-hour warmth, bloom flash and a soft vignette over the scene. */
function ScreenGrade({
  vs,
  letterboxed,
}: { readonly vs: VisualState; readonly letterboxed: boolean }) {
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      <div
        className="absolute inset-0 transition-opacity duration-700"
        style={{
          opacity: letterboxed ? 1 : 0.6,
          background: 'radial-gradient(ellipse at center, transparent 45%, rgba(10,6,2,0.55) 100%)',
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          opacity: vs.golden * 0.6,
          mixBlendMode: 'soft-light',
          background:
            'linear-gradient(160deg, rgba(255,196,110,0.95) 0%, rgba(255,150,60,0.6) 45%, rgba(120,60,20,0.5) 100%)',
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          opacity: vs.golden * 0.14,
          background: 'linear-gradient(180deg, #ffb45c 0%, transparent 70%)',
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          opacity: vs.bloom * 0.7,
          mixBlendMode: 'screen',
          background:
            'radial-gradient(ellipse at center, rgba(255,240,200,0.9) 0%, rgba(255,200,120,0.35) 35%, transparent 70%)',
        }}
      />
    </div>
  );
}

function Banner({
  title,
  subtitle,
  kidLine,
  icon: Icon,
  kid,
  reducedMotion,
  t,
}: {
  readonly title: string;
  readonly subtitle: string | null;
  readonly kidLine: string | null;
  readonly icon: LucideIcon;
  readonly kid: boolean;
  readonly reducedMotion: boolean;
  readonly t: Translate;
}) {
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-4">
      <section
        data-testid="cinematic-banner"
        {...appDom({ id: 'cinematic-banner', type: 'Banner', scope: 'dialog' })}
        aria-live="polite"
        aria-label={t('cinematic.label')}
        className={`${reducedMotion ? 'animate-fade-in-scale' : 'cinematic-banner-in'} relative flex flex-col items-center rounded-2xl border border-amber-300/45 bg-gradient-to-b from-stone-950/85 to-stone-900/80 text-center shadow-[0_0_70px_rgba(255,180,80,0.35)] backdrop-blur-sm ${
          kid
            ? 'w-[min(94vw,640px)] gap-3 px-6 py-7 sm:px-10 sm:py-9'
            : 'w-[min(92vw,580px)] gap-2 px-6 py-5 sm:px-10 sm:py-7'
        }`}
      >
        <span
          className="absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-amber-200/80 to-transparent"
          aria-hidden="true"
        />
        <span
          className="absolute inset-x-8 bottom-0 h-px bg-gradient-to-r from-transparent via-amber-200/80 to-transparent"
          aria-hidden="true"
        />
        {kid ? (
          <span className="flex size-16 items-center justify-center rounded-full bg-gradient-to-b from-amber-200 to-amber-500 text-stone-900 shadow-[0_0_30px_rgba(255,200,90,0.6)] sm:size-20">
            <Icon className="size-9 sm:size-11" aria-hidden="true" />
          </span>
        ) : (
          <span className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.3em] text-amber-200/85 uppercase">
            <Icon className="size-3.5" aria-hidden="true" />
            {t('cinematic.label')}
          </span>
        )}
        <h2
          className={`font-serif leading-tight text-balance text-amber-50 drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)] ${
            kid ? 'text-3xl sm:text-5xl' : 'text-2xl sm:text-4xl'
          }`}
        >
          {title}
        </h2>
        {kidLine && <p className="text-lg font-semibold text-amber-100 sm:text-2xl">{kidLine}</p>}
        {subtitle && (
          <p
            className={`text-pretty text-amber-100/80 ${kid ? 'text-sm sm:text-base' : 'text-sm sm:text-lg'}`}
          >
            {subtitle}
          </p>
        )}
      </section>
    </div>
  );
}

// ── particles (CSS; keyframes in styles/globals.css) ─────────────────────

/** Deterministic 0..1 noise so particles don't reshuffle on every render. */
function rand(i: number, salt: number): number {
  const x = Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

function Particles({ kind }: { readonly kind: ParticleKind }) {
  const items = useMemo(() => {
    if (kind === 'fireworks') {
      const bursts = 5;
      const rays = 14;
      const out: Array<{ key: string; style: CSSProperties; className: string }> = [];
      for (let b = 0; b < bursts; b++) {
        const left = 12 + rand(b, 1) * 76;
        const top = 10 + rand(b, 2) * 40;
        const hue = [42, 18, 200, 330, 50][b % 5];
        const delay = b * 0.55 + rand(b, 3) * 0.3;
        for (let r = 0; r < rays; r++) {
          out.push({
            key: `${b}-${r}`,
            className: 'cin-firework',
            style: {
              left: `${left}%`,
              top: `${top}%`,
              '--cin-angle': `${(360 / rays) * r}deg`,
              '--cin-dist': `${60 + rand(b * rays + r, 4) * 50}px`,
              background: `hsl(${hue} 95% 70%)`,
              boxShadow: `0 0 8px hsl(${hue} 95% 65%)`,
              animationDelay: `${delay}s`,
            } as CSSProperties,
          });
        }
      }
      return out;
    }
    const count = kind === 'sparkles' ? 42 : 26;
    return Array.from({ length: count }, (_, i) => {
      const size = kind === 'sparkles' ? 3 + rand(i, 5) * 5 : 30 + rand(i, 5) * 60;
      return {
        key: String(i),
        className: kind === 'sparkles' ? 'cin-sparkle' : 'cin-dust',
        style: {
          left: `${rand(i, 6) * 100}%`,
          top: `${kind === 'sparkles' ? 25 + rand(i, 7) * 70 : 45 + rand(i, 7) * 55}%`,
          width: `${size}px`,
          height: `${size}px`,
          animationDelay: `${rand(i, 8) * 2.5}s`,
          animationDuration: `${(kind === 'sparkles' ? 2.4 : 5) + rand(i, 9) * 2}s`,
        } as CSSProperties,
      };
    });
  }, [kind]);

  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      {items.map((p) => (
        <span key={p.key} className={`absolute rounded-full ${p.className}`} style={p.style} />
      ))}
    </div>
  );
}
